import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  Stack,
  TableCell,
  TableRow,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Add as AddIcon,
  Check as ApproveIcon,
  Send as PostIcon,
  ThumbDown as RejectIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { useAccessControl } from '../../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../../hooks/usePermissions';
import { ledgerAPI } from '../../../services/api';
import { formatAccountingAmount } from '../../../utils/formatters';
import { DataTable, useAppSnackbar, useConfirmDialog } from '../../common/TableDesign';
import ModulePageHeader from '../../common/ModulePageHeader';
import TablePagination from '../../common/TablePagination';
import ResponsiveRecordList, { EmptyRecordsState } from '../../common/ResponsiveRecordList';
import { FiscalYearPicker } from '../LedgerPickers';
import { BudgetPicker, BudgetStatusChip, budgetLabelOf, fmtDate, getErrorMessage, unwrapList } from './BudgetingShared';

const STATUSES = ['DRAFT', 'APPROVED', 'POSTED', 'REJECTED'];
const PAGE_SIZE = 15;

const emptyForm = () => ({
  from_budget_id: '',
  to_budget_id: '',
  amount: '',
  reason: '',
});

// Budget transfers (virements) move budget between two APPROVED lines
// without changing the total (verified against BudgetController):
// DRAFT -> APPROVED -> POSTED, with REJECTED as the terminal alternative.
// The list endpoint (GET transfers/all, optionally by fiscal year) is
// unpaginated and supports no search/status params, so this tab fetches once
// and filters client-side - unlike the LedgerListPage tabs.
const TransfersTab = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { can } = useAccessControl();
  const canCreate = can(RESOURCES.LEDGER.BUDGETING, ACTIONS.CREATE);
  const canApprove = can(RESOURCES.LEDGER.BUDGETING, ACTIONS.APPROVE);
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fiscalYearId, setFiscalYearId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(PAGE_SIZE);
  const [showCreate, setShowCreate] = useState(false);
  const [values, setValues] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);

  const showSnackbarRef = useRef(showSnackbar);
  showSnackbarRef.current = showSnackbar;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await ledgerAPI.getBudgetTransfers(fiscalYearId ? { fiscal_year_id: fiscalYearId } : {});
      setItems(unwrapList(res));
    } catch (error) {
      showSnackbarRef.current(getErrorMessage(error, 'Failed to fetch transfers'), 'error');
    } finally {
      setLoading(false);
    }
  }, [fiscalYearId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, statusFilter, fiscalYearId, size]);

  if (!can(RESOURCES.LEDGER.BUDGETING, ACTIONS.READ)) {
    return <Alert severity="error">You do not have permission to view Budget Transfers.</Alert>;
  }

  const idOf = (t) => t?.transfer_id ?? t?.id;

  const filtered = items.filter((t) => {
    if (statusFilter && String(t.status || '').toUpperCase() !== statusFilter) return false;
    if (searchTerm) {
      const hay = `${t.transfer_number || ''} ${t.reason || ''}`.toLowerCase();
      if (!hay.includes(searchTerm.toLowerCase())) return false;
    }
    return true;
  });
  const paged = filtered.slice((page - 1) * size, page * size);
  const pagination = {
    current_page: page,
    last_page: Math.max(1, Math.ceil(filtered.length / size)),
    per_page: size,
    total: filtered.length,
  };

  const submitCreate = async () => {
    if (!values.from_budget_id || !values.to_budget_id) {
      showSnackbar('Source and destination budget lines are required', 'error');
      return;
    }
    if (String(values.from_budget_id) === String(values.to_budget_id)) {
      showSnackbar('Source and destination must differ', 'error');
      return;
    }
    if (!(Number(values.amount) > 0)) {
      showSnackbar('Amount must be greater than zero', 'error');
      return;
    }
    try {
      setSaving(true);
      await ledgerAPI.createBudgetTransfer({
        from_budget_id: Number(values.from_budget_id),
        to_budget_id: Number(values.to_budget_id),
        amount: Number(values.amount),
        ...(values.reason?.trim() ? { reason: values.reason.trim() } : {}),
      });
      showSnackbar('Transfer created as DRAFT (both lines must be APPROVED)', 'success');
      setShowCreate(false);
      setValues(emptyForm());
      load();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const runWorkflow = (label, pastTense, fn) => {
    confirm({
      title: label,
      message: `Are you sure you want to ${label.toLowerCase()} this transfer?`,
      onConfirm: async () => {
        try {
          await fn();
          showSnackbar(`Transfer ${pastTense} successfully`, 'success');
          load();
        } catch (error) {
          showSnackbar(getErrorMessage(error), 'error');
        }
      },
    });
  };

  const fromLabel = (t) => budgetLabelOf(t.from_budget ?? t.fromBudget) || (t.from_budget_id ? `Line #${t.from_budget_id}` : '-');
  const toLabel = (t) => budgetLabelOf(t.to_budget ?? t.toBudget) || (t.to_budget_id ? `Line #${t.to_budget_id}` : '-');

  const rowMenuItems = (t) => {
    const id = idOf(t);
    const status = String(t.status || 'DRAFT').toUpperCase();
    const menuItems = [
      { icon: <ViewIcon fontSize="small" />, label: 'View detail', onClick: () => setDetail(t) },
    ];
    if (status === 'DRAFT' && canApprove) {
      menuItems.push({ icon: <ApproveIcon fontSize="small" color="primary" />, label: 'Approve', onClick: () => runWorkflow('Approve transfer', 'approved', () => ledgerAPI.approveBudgetTransfer(id)) });
    }
    if (status === 'APPROVED' && canApprove) {
      menuItems.push({ icon: <PostIcon fontSize="small" color="primary" />, label: 'Post (moves amounts)', onClick: () => runWorkflow('Post transfer', 'posted', () => ledgerAPI.postBudgetTransfer(id)) });
    }
    if (['DRAFT', 'APPROVED'].includes(status) && canApprove) {
      menuItems.push({ icon: <RejectIcon fontSize="small" color="error" />, label: 'Reject', danger: true, onClick: () => runWorkflow('Reject transfer', 'rejected', () => ledgerAPI.rejectBudgetTransfer(id)) });
    }
    return menuItems;
  };

  const columns = ['Transfer #', 'From', 'To', 'Amount', 'Status', 'Actions'];

  return (
    <Box>
      <ModulePageHeader
        title="Budget Transfers"
        subtitle="Virements between APPROVED budget lines - totals never change"
        actions={canCreate ? [
          <Button key="add" variant="contained" startIcon={<AddIcon />} onClick={() => { setValues(emptyForm()); setShowCreate(true); }} fullWidth={isMobile}>
            New Transfer
          </Button>,
        ] : null}
      />

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start' }}>
            <TextField
              size="small"
              placeholder="Search reason..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
              sx={{ flex: '1 1 220px', minWidth: 180 }}
            />
            <Box sx={{ width: 200 }}>
              <FiscalYearPicker label="Fiscal year" value={fiscalYearId} onChange={(id) => setFiscalYearId(id)} />
            </Box>
            <TextField
              size="small" select label="Status" value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)} sx={{ width: 160 }}
            >
              <MenuItem value="">All</MenuItem>
              {STATUSES.map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </TextField>
            {(searchTerm || statusFilter || fiscalYearId) && (
              <Button size="small" variant="outlined" color="inherit" onClick={() => { setSearchTerm(''); setStatusFilter(''); setFiscalYearId(''); }}>
                Clear filters
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {loading ? (
            <Box py={5} textAlign="center">
              <CircularProgress size={28} />
            </Box>
          ) : filtered.length === 0 ? (
            <EmptyRecordsState message="No budget transfers found." />
          ) : isMobile ? (
            <ResponsiveRecordList
              items={paged}
              renderItem={(t) => (
                <Stack spacing={1.25}>
                  <Box display="flex" justifyContent="space-between" gap={2}>
                    <Typography variant="body2" color="text.secondary">Transfer</Typography>
                    <Typography variant="body2" fontWeight={700} textAlign="right">{t.transfer_number || `TRF-${idOf(t)}`}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" gap={2}>
                    <Typography variant="body2" color="text.secondary">Amount</Typography>
                    <Typography variant="body2" textAlign="right">{formatAccountingAmount(t.amount)}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" gap={2} alignItems="center">
                    <Typography variant="body2" color="text.secondary">Status</Typography>
                    <BudgetStatusChip status={t.status} />
                  </Box>
                  <Box textAlign="right">
                    <Button size="small" onClick={(e) => { setMenuRow(t); setMenuAnchor(e.currentTarget); }}>Actions</Button>
                  </Box>
                </Stack>
              )}
            />
          ) : (
            <DataTable
              columns={columns}
              data={paged}
              loading={false}
              emptyMessage="No budget transfers found."
              renderRow={(t) => (
                <TableRow key={idOf(t)} hover>
                  <TableCell><strong>{t.transfer_number || `TRF-${idOf(t)}`}</strong></TableCell>
                  <TableCell>{fromLabel(t)}</TableCell>
                  <TableCell>{toLabel(t)}</TableCell>
                  <TableCell align="right">{formatAccountingAmount(t.amount)}</TableCell>
                  <TableCell><BudgetStatusChip status={t.status} /></TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={(e) => { setMenuRow(t); setMenuAnchor(e.currentTarget); }}>
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              )}
            />
          )}
          <TablePagination pagination={pagination} onPageChange={(p) => setPage(p)} onPageSizeChange={(s) => { setSize(parseInt(s, 10)); setPage(1); }} isMobile={isMobile} />
        </CardContent>
      </Card>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => { setMenuAnchor(null); setMenuRow(null); }} PaperProps={{ elevation: 2, sx: { minWidth: 190 } }}>
        {(menuRow ? rowMenuItems(menuRow) : []).map((it, i) => (
          <React.Fragment key={it.label || `item-${i}`}>
            {it.danger && i > 0 && <Divider />}
            <MenuItem onClick={() => { setMenuAnchor(null); it.onClick?.(); }} sx={it.danger ? { color: 'error.main' } : undefined}>
              {it.icon && <ListItemIcon>{it.icon}</ListItemIcon>}
              {it.label}
            </MenuItem>
          </React.Fragment>
        ))}
      </Menu>

      <Dialog open={showCreate} onClose={() => setShowCreate(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Budget Transfer</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid size={12}>
              <BudgetPicker label="From budget line (APPROVED)" value={values.from_budget_id} onChange={(id) => setValues((p) => ({ ...p, from_budget_id: id }))} required />
            </Grid>
            <Grid size={12}>
              <BudgetPicker label="To budget line (APPROVED)" value={values.to_budget_id} onChange={(id) => setValues((p) => ({ ...p, to_budget_id: id }))} required />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth size="small" type="number" label="Amount" value={values.amount}
                onChange={(e) => setValues((p) => ({ ...p, amount: e.target.value }))}
                inputProps={{ min: 0.01, step: 'any' }} required
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth size="small" multiline rows={2} label="Reason" value={values.reason}
                onChange={(e) => setValues((p) => ({ ...p, reason: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitCreate} disabled={saving}>
            {saving ? 'Saving...' : 'Create Draft'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Transfer {detail ? `TRF-${idOf(detail)}` : ''}</DialogTitle>
        <DialogContent>
          {detail && (
            <>
              <Typography variant="body2" color="text.secondary">{fmtDate(detail.created_at)}</Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>From: {fromLabel(detail)}</Typography>
              <Typography variant="body2">To: {toLabel(detail)}</Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>{detail.reason || '-'}</Typography>
              <Box display="flex" justifyContent="flex-end" gap={3} sx={{ mt: 2 }}>
                <Typography variant="body2">Amount: <strong>{formatAccountingAmount(detail.amount)}</strong></Typography>
                <BudgetStatusChip status={detail.status} />
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {ConfirmDialogComponent}
      {SnackbarComponent}
    </Box>
  );
};

export default TransfersTab;
